import { Grid, Card, CardHeader, CardContent, Button, Stack } from '@mui/material';
import { Verified, EventRepeat, ReportProblem, Autorenew, AccountBalance, PendingActions, Download } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { MiniBarChart } from '../../components/MiniBarChart';
import { EmptyState } from '../../components/EmptyState';
import { amcAnalyticsApi } from '../../api/evcpdAmc.api';
import { useAuth } from '../../hooks/useAuth';

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;
const n = (x = 0) => Number(x).toLocaleString('en-IN');

function ChartCard({ title, data, fmt }: { title: string; data: Array<{ label: string; value: number }>; fmt?: (v: number) => string }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader title={title} titleTypographyProps={{ variant: 'subtitle1' }} />
      <CardContent>{data.length ? <MiniBarChart data={data.map((d) => ({ ...d, color: '#1e40af' }))} valueFormatter={fmt ?? n} /> : <EmptyState title="No data yet" dense />}</CardContent>
    </Card>
  );
}

export function AmcDashboardPage() {
  const { hasPermission } = useAuth();
  const { data } = useQuery({ queryKey: ['amc', 'analytics'], queryFn: () => amcAnalyticsApi.dashboard() });
  const k = data?.kpis;
  const c = data?.charts;

  return (
    <>
      <PageHeader
        title="Executive AMC Dashboard"
        subtitle="AMC, preventive maintenance, SLA, renewals and revenue analytics."
        action={
          hasPermission('evcpd_amc_contract.read') && (
            <Stack direction="row" spacing={1}>
              <Button startIcon={<Download />} variant="outlined" onClick={() => amcAnalyticsApi.download('/evcpd-amc/analytics/export', 'xlsx')}>Excel</Button>
              <Button startIcon={<Download />} variant="outlined" onClick={() => amcAnalyticsApi.download('/evcpd-amc/analytics/export', 'pdf')}>PDF</Button>
            </Stack>
          )
        }
      />
      <Grid container spacing={2} mb={1}>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#1e40af" label="Total AMC" value={n(k?.totalContracts)} icon={<Verified />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#16a34a" label="Active" value={n(k?.active)} icon={<Verified />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#d97706" label="Expiring" value={n(k?.expiring)} icon={<Autorenew />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#0d9488" label="Renewed" value={n(k?.renewed)} icon={<Autorenew />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#7c3aed" label="AMC Revenue" value={inr(k?.amcRevenue)} icon={<AccountBalance />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#dc2626" label="SLA Breaches" value={n(k?.slaBreaches)} icon={<ReportProblem />} /></Grid>
      </Grid>
      <Grid container spacing={2} mb={1}>
        <Grid item xs={6} sm={3}><StatCard color="#d97706" label="PM Due" value={n(k?.pmDue)} icon={<EventRepeat />} /></Grid>
        <Grid item xs={6} sm={3}><StatCard color="#16a34a" label="PM Completed" value={n(k?.pmCompleted)} icon={<EventRepeat />} /></Grid>
        <Grid item xs={6} sm={3}><StatCard color="#0284c7" label="PM Pending" value={n(k?.pmPending)} icon={<PendingActions />} /></Grid>
        <Grid item xs={6} sm={3}><StatCard color="#0d9488" label="PM Compliance" value={`${c?.pmCompliancePct ?? 0}%`} icon={<EventRepeat />} hint={`${n(k?.renewalPipeline)} renewal pipeline`} /></Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}><ChartCard title="State-wise AMC Revenue" data={c?.stateWiseRevenue ?? []} fmt={inr} /></Grid>
        <Grid item xs={12} md={6}><ChartCard title="Contract Status Distribution" data={c?.contractStatusDistribution ?? []} /></Grid>
        <Grid item xs={12} md={6}><ChartCard title="Revenue Trend" data={c?.revenueTrend ?? []} fmt={inr} /></Grid>
        <Grid item xs={12} md={6}><ChartCard title="Renewal Trend" data={c?.renewalTrend ?? []} /></Grid>
        <Grid item xs={12} md={6}><ChartCard title="SLA Breach Trend" data={c?.slaTrend ?? []} /></Grid>
        <Grid item xs={12} md={6}><ChartCard title="Technician Performance (PM completed)" data={c?.technicianPerformance ?? []} /></Grid>
        <Grid item xs={12} md={6}><ChartCard title="OEM Performance (contracts)" data={(c?.oemPerformance ?? []).map((o) => ({ label: o.label, value: o.value }))} /></Grid>
      </Grid>
    </>
  );
}
